import { useMemo, useCallback } from 'react';
import { MaintenanceDraftWithDone, ComponentWithReplacement } from '../types';
import { MaintenanceTemplate, ComponentLifetime, TemplatesByType, getIntervalForDirection, getLifetimeForDirection } from '../../TemplatesEditor/types';
import { Building } from '../../../../types/protocols';
import { MaintenanceIntervalType } from '../../../../types/Enums';
import { formatDateToString, getActivityDisplayCost, getReplacementDisplayCost, customRateOf, unitCountOf, COST_SOURCE_LABELS } from '../../../../Helpers/maintenanceCostUtils';
import { BaseComponentImpl } from '../../../../types/implementations';
import { resolveCardinalDirection } from '../utils/cardinalDirectionResolver';
import { isConditionSet } from '../../../../Helpers/conditionUtils';
import { templateOfferedTo } from '../../../../Helpers/maintenanceTemplates';

/** The cost comment on its own line under the explanation */
const withCostComment = (text: string, comment: string | undefined): string =>
    comment ? `${text}\n${comment}` : text;

interface UseDisplayHelpersProps {
    maintenanceDraft: MaintenanceDraftWithDone;
    building: Building | null;
    componentLifetimes: Record<string, ComponentLifetime>;
    maintenanceTemplates: TemplatesByType;
    buildingKey?: string;
    getTemplateById?: (id: string) => MaintenanceTemplate | null;
}

interface UseDisplayHelpersReturn {
    getDisplayReplacementYear: (plan: ComponentWithReplacement) => string;
    getDisplayMaintenanceDate: (componentKey: string, templateId: string, calculatedDate: Date | null) => string;
    getDisplayMaintenanceInterval: (componentKey: string, templateId: string, template: MaintenanceTemplate) => string;
    getDisplayBaseInterval: (componentKey: string, template: MaintenanceTemplate) => { value: string, type: MaintenanceIntervalType };
    getDisplayLifetime: (componentKey: string, lifetime: ComponentLifetime) => string;
    getDisplayReplacementCost: (componentKey: string) => string;
    getDisplayMaintenanceCost: (componentKey: string, templateId: string) => string;
    getReplacementCostExplanation: (componentKey: string) => string;
    getMaintenanceCostExplanation: (componentKey: string, templateId: string) => string;
    getRelevantTemplates: (componentType: string) => MaintenanceTemplate[];
}

export const useDisplayHelpers = ({
    maintenanceDraft,
    building,
    componentLifetimes,
    maintenanceTemplates,
    buildingKey,
    getTemplateById
}: UseDisplayHelpersProps): UseDisplayHelpersReturn => {

    // Helper to find template - first tries by component type, then falls back to flat lookup
    const findTemplate = (componentType: string, templateId: string): MaintenanceTemplate | null => {
        // First try the typed lookup (templates for this component type)
        const templates = maintenanceTemplates[componentType];
        const templatesArray = Array.isArray(templates) ? templates : [];
        const template = templatesArray.find(t => t.id === templateId);
        if (template) return template;

        // Fall back to flat lookup for cross-type templates
        if (getTemplateById) {
            return getTemplateById(templateId);
        }

        return null;
    };

    const getDisplayReplacementYear = (plan: ComponentWithReplacement): string => {
        const componentKey = plan.component.key;
        if (maintenanceDraft.components[componentKey]?.replacementYear !== undefined) {
            return maintenanceDraft.components[componentKey].replacementYear!.toString();
        }
        if (plan.replacementDate) {
            return plan.replacementDate.getFullYear().toString();
        }
        return '';
    };

    const getDisplayMaintenanceDate = (componentKey: string, templateId: string, calculatedDate: Date | null): string => {
        const activity = maintenanceDraft.components[componentKey]?.maintenanceActivities?.[templateId];
        if (activity?.nextDate) {
            return activity.nextDate;
        }
        if (calculatedDate) {
            return formatDateToString(calculatedDate);
        }
        return '';
    };

    const getDisplayMaintenanceInterval = (componentKey: string, templateId: string, template: MaintenanceTemplate): string => {
        const activity = maintenanceDraft.components[componentKey]?.maintenanceActivities?.[templateId];
        if (activity?.intervalValue !== undefined) {
            return activity.intervalValue.toString();
        }
        
        const componentData = Object.values(building?.model?.structure?.components || {}).find(c => c.key === componentKey);
        if (componentData) {
            const component = new BaseComponentImpl(componentData);
            const directionalInterval = getIntervalForDirection(template, resolveCardinalDirection(component, building));
            return directionalInterval.interval_value.toString();
        }
        
        return template.interval_value.toString();
    };

    const getDisplayBaseInterval = (componentKey: string, template: MaintenanceTemplate): { value: string, type: MaintenanceIntervalType } => {
        const componentData = Object.values(building?.model?.structure?.components || {}).find(c => c.key === componentKey);
        if (componentData) {
            const component = new BaseComponentImpl(componentData);
            const directionalInterval = getIntervalForDirection(template, resolveCardinalDirection(component, building));
            
            return {
                value: directionalInterval.interval_value.toString(),
                type: directionalInterval.interval_type
            };
        }
        return {
            value: template.interval_value.toString(),
            type: template.interval_type
        };
    };

    const getDisplayLifetime = (componentKey: string, lifetime: ComponentLifetime): string => {
        const componentData = Object.values(building?.model?.structure?.components || {}).find(c => c.key === componentKey);
        if (componentData) {
            const component = new BaseComponentImpl(componentData);
            const directionalLifetime = getLifetimeForDirection(lifetime, component.cardinalDirection?.value || null);
            return directionalLifetime.expectedLifetimeYears.toString();
        }
        return lifetime.expectedLifetimeYears.toString();
    };

    const getDisplayReplacementCost = (componentKey: string): string => {
        const componentData = maintenanceDraft.components[componentKey];
        if (!componentData) return '';

        const component = building?.model?.structure?.components?.[componentKey];
        if (!component) return '';

        const componentImpl = new BaseComponentImpl(component);
        const componentType = componentImpl.specificType?.value;
        if (!componentType) return '';

        const lifetime = componentLifetimes[componentType];
        if (!lifetime) return '';

        // Use getReplacementDisplayCost for dynamic calculation in draft
        const cost = getReplacementDisplayCost(componentData, componentImpl, lifetime, building!, true);
        return cost > 0 ? cost.toString() : '';
    };

    const getDisplayMaintenanceCost = (componentKey: string, templateId: string): string => {
        const activity = maintenanceDraft.components[componentKey]?.maintenanceActivities?.[templateId];
        if (!activity) return '';

        const component = building?.model?.structure?.components?.[componentKey];
        if (!component) return '';

        const componentImpl = new BaseComponentImpl(component);
        const componentType = componentImpl.specificType?.value;
        if (!componentType) return '';

        // Use findTemplate helper for cross-type template support
        const template = findTemplate(componentType, templateId);
        if (!template) return '';

        // Use getActivityDisplayCost for dynamic calculation in draft
        const cost = getActivityDisplayCost(activity, componentImpl, template, building!, true);
        return cost > 0 ? cost.toString() : '';
    };

    const getReplacementCostExplanation = (componentKey: string): string => {
        const component = building?.model?.structure?.components?.[componentKey];
        if (!component) {
            return 'Komponent hittades inte';
        }

        const componentData = maintenanceDraft.components[componentKey];
        const componentImpl = new BaseComponentImpl(component);
        const componentType = componentImpl.specificType?.value;

        if (!componentType) {
            return 'Komponenttyp saknas';
        }

        const lifetime = componentLifetimes[componentType];
        const hasCondition = isConditionSet(componentImpl.condition?.value?.value);
        const hasLifetime = lifetime?.expectedLifetimeYears !== undefined && lifetime.expectedLifetimeYears > 0;

        if (!hasCondition) {
            return 'Ingen skickbedömning tillgänglig';
        }

        if (!hasLifetime) {
            return `Ingen livslängdsdata tillgänglig för ${componentType}`;
        }

        if (componentData?.replacementCostSource === 'custom') {
            return withCostComment(COST_SOURCE_LABELS.custom, componentData.replacementCostComment);
        }

        const ownRate = customRateOf(componentData?.replacementCostSource, componentData?.replacementCustomUnitPrice);
        if (ownRate === undefined && !lifetime.replacementCost) {
            return `Ingen ersättningskostnad angiven i mallen för ${componentType}`;
        }
        const rate = ownRate ?? lifetime.replacementCost ?? 0;
        const prefix = ownRate === undefined ? 'Från mall' : COST_SOURCE_LABELS.customRate;
        const comment = ownRate === undefined ? undefined : componentData?.replacementCostComment;

        // Cost can be calculated - show helpful info
        if (lifetime.costUnit === 'per_sqm') {
            const area = componentImpl.totalArea(building);
            if (area && area > 0) {
                return withCostComment(`${prefix}: ${rate.toLocaleString('sv-SE')} kr/m² × ${area.toLocaleString('sv-SE')} m²`, comment);
            } else {
                return 'Area saknas för komponenten';
            }
        } else if (lifetime.costUnit === 'per_lm') {
            const length = componentImpl.linearMeters?.value;
            if (length && length > 0) {
                return withCostComment(`${prefix}: ${rate.toLocaleString('sv-SE')} kr/lm × ${length.toLocaleString('sv-SE')} lm`, comment);
            } else {
                return 'Löpmeter saknas för komponenten';
            }
        }

        if (componentData?.replacementCustomUnitCount !== undefined) {
            return withCostComment(`${prefix}: ${rate.toLocaleString('sv-SE')} kr/st × ${unitCountOf(componentData.replacementCustomUnitCount).toLocaleString('sv-SE')} st (anpassat)`, comment);
        }
        return withCostComment(`${prefix}: ${rate.toLocaleString('sv-SE')} kr`, comment);
    };

    const getMaintenanceCostExplanation = (componentKey: string, templateId: string): string => {
        const component = building?.model?.structure?.components?.[componentKey];
        if (!component) {
            return 'Komponent hittades inte';
        }

        const activity = maintenanceDraft.components[componentKey]?.maintenanceActivities?.[templateId];
        const componentImpl = new BaseComponentImpl(component);
        const componentType = componentImpl.specificType?.value;

        if (!componentType) {
            return 'Komponenttyp saknas';
        }

        // Use findTemplate helper for cross-type template support
        const template = findTemplate(componentType, templateId);

        if (!template) {
            return 'Underhållsmall hittades inte';
        }

        const hasCondition = isConditionSet(componentImpl.condition?.value?.value);
        const lifetime = componentLifetimes[componentType!];
        const hasLifetime = lifetime?.expectedLifetimeYears !== undefined && lifetime.expectedLifetimeYears > 0;

        if (!hasCondition) {
            return 'Ingen skickbedömning tillgänglig';
        }

        if (!hasLifetime) {
            return `Ingen livslängdsdata tillgänglig för ${componentType}`;
        }

        if (activity?.costSource === 'custom') {
            return withCostComment(COST_SOURCE_LABELS.custom, activity.costComment);
        }

        const ownRate = customRateOf(activity?.costSource, activity?.customUnitPrice);
        if (ownRate === undefined && !template.cost) {
            return `Ingen kostnad angiven i mallen "${template.name}"`;
        }
        const rate = ownRate ?? template.cost ?? 0;
        const prefix = ownRate === undefined ? 'Från mall' : COST_SOURCE_LABELS.customRate;
        const comment = ownRate === undefined ? undefined : activity?.costComment;

        // Cost can be calculated - show helpful info
        if (template.cost_unit === 'per_sqm') {
            // Check for custom quantity first
            if (activity?.customQuantity !== undefined && activity.customQuantity > 0) {
                return withCostComment(`${prefix}: ${rate.toLocaleString('sv-SE')} kr/m² × ${activity.customQuantity.toLocaleString('sv-SE')} m² (anpassad)`, comment);
            }
            const area = componentImpl.totalArea(building);
            if (area && area > 0) {
                return withCostComment(`${prefix}: ${rate.toLocaleString('sv-SE')} kr/m² × ${area.toLocaleString('sv-SE')} m²`, comment);
            } else {
                return 'Area saknas för komponenten';
            }
        } else if (template.cost_unit === 'per_lm') {
            // Check for custom quantity first
            if (activity?.customQuantity !== undefined && activity.customQuantity > 0) {
                return withCostComment(`${prefix}: ${rate.toLocaleString('sv-SE')} kr/lm × ${activity.customQuantity.toLocaleString('sv-SE')} lm (anpassad)`, comment);
            }
            const length = componentImpl.linearMeters?.value;
            if (length && length > 0) {
                return withCostComment(`${prefix}: ${rate.toLocaleString('sv-SE')} kr/lm × ${length.toLocaleString('sv-SE')} lm`, comment);
            } else {
                return 'Löpmeter saknas för komponenten';
            }
        }

        if (activity?.customUnitCount !== undefined) {
            return withCostComment(`${prefix}: ${rate.toLocaleString('sv-SE')} kr/st × ${unitCountOf(activity.customUnitCount).toLocaleString('sv-SE')} st (anpassat)`, comment);
        }
        return withCostComment(`${prefix}: ${rate.toLocaleString('sv-SE')} kr`, comment);
    };

    const getRelevantTemplates = useCallback((componentType: string): MaintenanceTemplate[] => {
        const templates = maintenanceTemplates[componentType];

        if (Array.isArray(templates)) {
            // The templates offered to this building: not archived, global or its own
            return templates.filter(template => templateOfferedTo(template, buildingKey));
        }
        return [];
    }, [maintenanceTemplates, buildingKey]);

    return {
        getDisplayReplacementYear,
        getDisplayMaintenanceDate,
        getDisplayMaintenanceInterval,
        getDisplayBaseInterval,
        getDisplayLifetime,
        getDisplayReplacementCost,
        getDisplayMaintenanceCost,
        getReplacementCostExplanation,
        getMaintenanceCostExplanation,
        getRelevantTemplates
    };
};
